import type {
	RichRiskModelValue,
	TagValue,
	UserInstrumentClassificationDto,
	UserInstrumentDto,
} from "@eu.mdotm.api/sphere";
import { spawnInstrumentClassificationDialog } from "$root/functional-areas/instruments/instrument-classification/InstrumentClassificationDialog";
import {
	AgGridTooltipWrapper,
	richTextClipboardItem,
	useAgGridRowListener,
} from "$root/third-party-integrations/ag-grid/ag-grid";
import { qualifier } from "$root/utils";
import { Button } from "@mdotm/shadcn/mdotm/components";
import { Icon, Row } from "@mdotm/shadcn/legacy/components";
import { themeCSSVars } from "@mdotm/shadcn/legacy/themes";
import { builtInSortFnFor, noop } from "@mdotm/shadcn/legacy/utils";
import type { ColDef } from "ag-grid-community";
import type { CustomCellRendererProps, CustomTooltipProps } from "ag-grid-react";
import { tagColors } from "../../colors";
import type { ColumnOverrideParams } from "../../column/common-columns-definition";
import { TagCell, TagWeightList } from "./tag-views";
import React from "react";

export function riskModelColDef<T extends UserInstrumentDto = UserInstrumentDto>(
	opt?: Omit<
		ColumnOverrideParams<T, Array<RichRiskModelValue>>,
		"tooltipComponent" | "cellRenderer" | "cellEditor" | "tooltipValueGetter" | "valueGetter" | "valueSetter"
	>,
): ColDef<T, Array<RichRiskModelValue>> {
	return {
		colId: "RISK_MODEL",
		headerName: "Risk model",
		field: "riskModel" satisfies keyof UserInstrumentDto as any,
		tooltipField: "riskModel" satisfies keyof UserInstrumentDto as any,
		tooltipComponent: riskModelTooltip(),
		cellRenderer: riskModelCell(),
		cellEditor: riskModelTooltip(),
		cellEditorPopup: true,
		cellEditorPopupPosition: "under",
		onCellDoubleClicked(event) {
			const row = event;
			spawnInstrumentClassificationDialog({
				defaultInstrumentProxies: row.value ?? [],
				onSubmitAsync: ({ indices, weights }) => {
					const proxyIndexes = indices.map(
						(props): RichRiskModelValue => ({
							...props,
							name: props.instrument,
							weight: props.ticker ? weights.get(props.ticker)?.toNumber() : undefined,
						}),
					);
					row.node?.setDataValue("RISK_MODEL", proxyIndexes);
				},
			}).catch(noop);
		},
		valueFormatter: ({ value }) =>
			!value
				? ""
				: richTextClipboardItem({
						html: {
							style: `body,span { font:sans-serif }`,
							bodyContent: value.map((proxy, i) => (
								<React.Fragment key={`${proxy.name}-${i}`}>
									<span
										style={{
											backgroundColor: tagColors[i % tagColors.length].badgeColor(),
										}}
									>
										<span>{proxy.name}</span> <span>({proxy.weight}%)</span>
									</span>
									{i < value.length - 1 ? " " : ""}
								</React.Fragment>
							)),
						},
						plain: proxiesToClipboardStr(value),
					}),
		// TODO: value parser? the API seems to require a complete instrument, but if BE
		// can change it to accept a simple {ticker, weight} pair it should work by uncommenting the code
		// below and removing the suppressPaste
		// valueParser: ({ newValue, oldValue }) => {
		// 	if (newValue.trim() === "") {
		// 		return [];
		// 	}
		// 	// matches strings formatted as "Proxy1 (11%) Proxy2 (20%)"
		// 	// or "Proxy1 (11%); Proxy2 (20%)", with various combination
		// 	// of number formats, such as 1.20, 1., 1, .20
		// 	const regex = /\s*(.+?)\s+\(((\.\d+)|(\d+(\.\d*)?))%\);?\s*/g;
		// 	const rawProxies: RichRiskModelValue[] = [];
		// 	let match;
		// 	while ((match = regex.exec(newValue))) {
		// 		rawProxies.push({
		// 			ticker: match[1],
		// 			weight: Number(match[2]),
		// 		});
		// 	}
		// 	const filtered = rawProxies.filter((proxy) => !Number.isNaN(proxy.weight));
		// 	if (filtered.length < rawProxies.length) {
		// 		return oldValue; // invalid tag detected, preventing paste as it might be cause unwanted changes
		// 	}
		// 	const normalizedRiskModel = normalizeRiskModel(filtered);

		// 	return normalizedRiskModel;
		// },
		valueParser({ oldValue }) {
			return oldValue;
		},
		suppressPaste: true,
		...(opt ?? {}),
	};
}

function proxiesToClipboardStr(value: RichRiskModelValue[]): string {
	return value.map((tag) => `${tag.name!} (${tag.weight}%)`).join("; ");
}

function proxiesToTags(proxies: RichRiskModelValue[] | undefined): [UserInstrumentClassificationDto, Array<TagValue>] {
	return [
		{
			dataVisualization: "BAR_CHART",
			options:
				proxies?.map((proxy, i) => ({
					option: proxy.name!,
					color: tagColors[i % tagColors.length].key,
				})) ?? [],
		},
		proxies
			?.map(
				(proxy): TagValue => ({
					option: proxy.name!,
					weight: proxy.weight!,
				}),
			)
			.sort(builtInSortFnFor("weight", "desc")) ?? [],
	];
}

function riskModelCell() {
	return function cellRenderer(
		row: CustomCellRendererProps<UserInstrumentDto, Array<RichRiskModelValue>>,
	): React.JSX.Element | string {
		if (row.value == null || row.value.length === 0) {
			return "";
		}

		const [classification, valueAsTags] = proxiesToTags(row.value);

		return <TagCell classification={classification} values={valueAsTags} />;
	};
}

function riskModelTooltip() {
	return function TooltipComponent(
		row: CustomTooltipProps<UserInstrumentDto, Array<RichRiskModelValue>>,
	): React.JSX.Element | string {
		useAgGridRowListener(row, "cellEditingStarted", () => row.hideTooltipCallback?.());

		if (row.value == null || row.value.length === 0) {
			return "";
		}

		const [classification, valueAsTags] = proxiesToTags(row.value);

		return (
			<AgGridTooltipWrapper className="min-w-[224px]">
				<TagWeightList
					showUntagged
					options={classification.options}
					values={valueAsTags}
					visualization="TAG_VIEW_WITH_COLOR"
				/>
				<Row
					onClick={(e) => e.stopPropagation()}
					flexGrow={0}
					alignItems="center"
					justifyContent="flex-end"
					className="pt-2"
				>
					<Button
						palette="tertiary"
						size="x-small"
						style={{
							color: themeCSSVars.palette_N600,
						}}
						data-qualifier={qualifier.instrumentsCustomisation.riskModelCell.editButton}
						onClick={() => {
							row.api.stopEditing();
							spawnInstrumentClassificationDialog({
								defaultInstrumentProxies: row.value ?? [],
								onSubmitAsync: ({ indices, weights }) => {
									const proxyIndexes = indices.map(
										(props): RichRiskModelValue => ({
											...props,
											name: props.instrument,
											weight: props.ticker ? weights.get(props.ticker)?.toNumber() : undefined,
										}),
									);
									row.node?.setDataValue("RISK_MODEL", proxyIndexes);
								},
							}).catch(noop);
						}}
					>
						<Icon icon="Edit" size={16} color={themeCSSVars.palette_N500} />
						&nbsp; Edit
					</Button>
				</Row>
			</AgGridTooltipWrapper>
		);
	};
}
